/** 指标搜索近义词：用户输入词 → 系统指标名称 */
const METRIC_SEARCH_SYNONYMS = [
  { keyword: '活跃', metric: 'DAU' },
  { keyword: '日活', metric: 'DAU' },
  { keyword: '月活', metric: 'MAU' },
  { keyword: '留存', metric: 'retention' },
  { keyword: '收入', metric: 'revenue' },
  { keyword: '销量', metric: 'units' },
  { keyword: '愿望单', metric: 'wish' },
] as const;

/** 长 keyword 优先匹配，避免短词误命中 */
const SORTED_METRIC_SYNONYMS = [...METRIC_SEARCH_SYNONYMS].sort(
  (a, b) => b.keyword.length - a.keyword.length,
);

const METRICS_TEXT_FIELDS = ['metric', 'metric_cn', 'metric_definition'] as const;

const SOURCES_TEXT_FIELDS = [
  'data_source',
  'feature',
  'raw_data',
  'data_usage',
  'service_summary',
  'industry_coverage',
] as const;

type TSearchableRecord = Record<string, unknown>;

const SHORT_TOKEN_PATTERN = /^[A-Z]{2,4}$/;
const shortTokenRegexCache = new Map<string, RegExp>();

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getShortTokenRegex = (token: string): RegExp => {
  const upper = token.toUpperCase();
  let regex = shortTokenRegexCache.get(upper);
  if (!regex) {
    regex = new RegExp(`\\b${escapeRegExp(upper)}\\b`, 'i');
    shortTokenRegexCache.set(upper, regex);
  }
  return regex;
};

/** 短英文码用词边界匹配，其余用子串匹配 */
const matchSearchToken = (fieldValue: string, token: string): boolean => {
  if (!fieldValue || !token) {
    return false;
  }
  const upperToken = token.toUpperCase();
  if (SHORT_TOKEN_PATTERN.test(upperToken)) {
    return getShortTokenRegex(upperToken).test(fieldValue);
  }
  return fieldValue.toUpperCase().includes(upperToken);
};

const matchAnyToken = (fieldValue: string, tokens: readonly string[]): boolean =>
  tokens.some(token => matchSearchToken(fieldValue, token));

/** 从用户输入解析近义词对应的指标名（支持子串，如「xxx活跃xxx」→ DAU） */
export const resolveMetricNamesFromInput = (input: string): string[] => {
  const trimmed = input.trim();
  if (!trimmed) {
    return [];
  }

  const metrics = new Set<string>();
  SORTED_METRIC_SYNONYMS.forEach(({ keyword, metric }) => {
    if (trimmed.includes(keyword)) {
      metrics.add(metric);
    }
  });
  return Array.from(metrics);
};

const filterByFields = <T extends TSearchableRecord>(
  records: T[],
  tokens: readonly string[],
  fieldKeys: readonly string[],
): T[] => {
  if (!tokens.length) {
    return records;
  }
  return records.filter(record =>
    fieldKeys.some(key => matchAnyToken(String(record[key] ?? ''), tokens)),
  );
};

/** Metrics 页：近义词仅匹配 metric 列；普通搜索覆盖 metric / metric_cn / metric_definition */
export const filterMetricsRecordsBySearch = <T extends TSearchableRecord>(
  records: T[],
  input: string,
): T[] => {
  const trimmed = input.trim();
  if (!trimmed) {
    return records;
  }

  const synonymMetrics = resolveMetricNamesFromInput(trimmed);
  if (synonymMetrics.length > 0) {
    return records.filter(record =>
      synonymMetrics.some(metric =>
        matchSearchToken(String(record.metric ?? ''), metric),
      ),
    );
  }

  return filterByFields(records, [trimmed], METRICS_TEXT_FIELDS);
};

/** Sources 页：近义词按转换后的指标名搜索；普通搜索按数据源及描述字段 */
export const filterSourcesRecordsBySearch = <T extends TSearchableRecord>(
  records: T[],
  input: string,
): T[] => {
  const trimmed = input.trim();
  if (!trimmed) {
    return records;
  }

  const synonymMetrics = resolveMetricNamesFromInput(trimmed);
  const tokens = synonymMetrics.length > 0 ? synonymMetrics : [trimmed];
  return filterByFields(records, tokens, SOURCES_TEXT_FIELDS);
};
